/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React, { useState, useMemo, ChangeEvent } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { RadioButtonGroup, RadioButton, TextArea, ComboBox, Link } from 'carbon-addons-iot-react';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';

import { COLUMNTYPE_LITERAL, DOCS_LINKS } from '../../../../Common/constants/constants';
import { messages as comboBoxMessages } from '../../../../Common/components/ComboBoxFormik/ComboBoxFormik';
import { DataItemType } from '../../../types/entitiesTypes';
import { Card, CardOnChangeType, ThirdPartyContentTypes } from '../../../types/dashboardTypes';
import { isValidHttpsURL, isValidHttpURL } from '../../../../utils/Utils';

import { isDataItemDimension } from 'dataFormatterFunctions';

const messages = defineMessages({
  addContentFrom: 'Add content from',
  url: 'URL',
  urlPlaceholder: 'Specify the URL for the third-party content.',
  dimension: 'Dimension',
  ensureCSP:
    'Ensure that you also add the URL origin to the Content Security Policy HTTP response header (CSP).',
  learnAboutCSP: 'Learn more about CSP',
  openURLOnCardClick: 'Open the URL when card is clicked',
  selectDimension: 'Select the dimension',
  selectDimensionPlaceHolder: 'Filter',
  specifyValidURL: 'Specify a valid URL',
  httpNotSupported: 'Specify a URL that uses HTTPS protocol.',
});

type ThirdPartyCardEditContentTypes = {
  onChange: CardOnChangeType;
  cardConfig: Card;
  dataItems?: DataItemType[];
  entityTypeName?: string;
  entityId?: string;
  docURL?: string;
};

const defaultProps = {
  dataItems: [],
  entityTypeName: null,
  entityId: null,
  docURL: null,
};

const ThirdPartyCardEditContent = ({
  onChange,
  cardConfig,
  dataItems,
  entityTypeName,
  entityId,
  docURL,
}: ThirdPartyCardEditContentTypes): JSX.Element => {
  const { type, value: cardValue } = cardConfig.customContent || {
    type: ThirdPartyContentTypes.URL,
  };
  const { formatMessage } = useIntl();

  const dimensionsItems = useMemo(
    () =>
      dataItems
        .filter(dataItem => isDataItemDimension(dataItem))
        .filter(dataItem => dataItem.columnType === COLUMNTYPE_LITERAL)
        .map(dim => ({ id: dim.name, text: dim.name })) || [],
    [dataItems]
  );

  const helperText = useMemo(
    () => (
      <>
        <div className="third-party-card-edit-content__helperText">
          {formatMessage(messages.ensureCSP)}
        </div>
        <Link
          className="third-party-card-edit-content__helperTextLink"
          href={`${docURL}${DOCS_LINKS.DASHBOARDS.THIRD_PARTY_CARD}`}
          target="_blank"
          size="sm">
          {formatMessage(messages.learnAboutCSP)}
        </Link>
      </>
    ),
    [formatMessage, docURL]
  );

  const [value, setValue] = useState(cardValue || '');
  const [sourceType, setSourceType] = useState(type || ThirdPartyContentTypes.URL);

  const delayedOnChange = debounce(input => onChange(input), 1000);

  return (
    <div className="third-party-card-edit-content">
      <div className="third-party-card-edit-content__separator" />
      <RadioButtonGroup
        legendText={formatMessage(messages.addContentFrom)}
        name="src-type"
        defaultSelected={sourceType}
        labelPosition="right"
        onChange={(val): void => {
          setSourceType(val);
          setValue('');
          delayedOnChange({
            ...omit(cardConfig, 'dataSource'),
            customContent: {
              type: val,
            },
          });
        }}
        orientation="vertical"
        valueSelected={sourceType}>
        <RadioButton
          id={ThirdPartyContentTypes.URL}
          value={ThirdPartyContentTypes.URL}
          labelText={formatMessage(messages.url)}
        />
        <RadioButton
          id={ThirdPartyContentTypes.DIMENSION}
          value={ThirdPartyContentTypes.DIMENSION}
          labelText={formatMessage(messages.dimension)}
          disabled={!(entityTypeName && entityId)} // Dimension radio button is visible only to device level dashbaords
        />
      </RadioButtonGroup>
      {sourceType === ThirdPartyContentTypes.URL ? (
        <div>
          <TextArea
            id="srcURL"
            name="srcURL"
            light
            onChange={(e: ChangeEvent<HTMLTextAreaElement>): void => {
              setValue(e.target.value);
              delayedOnChange({
                ...omit(cardConfig, 'dataSource'),
                customContent: {
                  type: ThirdPartyContentTypes.URL,
                  value: e.target.value,
                },
              });
            }}
            value={value}
            labelText=""
            hideLabel
            placeholder={formatMessage(messages.urlPlaceholder)}
            invalid={value && !isValidHttpsURL(value)}
            invalidText={
              isValidHttpURL(value)
                ? formatMessage(messages.httpNotSupported)
                : formatMessage(messages.specifyValidURL)
            }
            helperText={helperText}
          />
        </div>
      ) : (
        <ComboBox
          itemToString={(item): string => item?.text}
          translateWithId={(id): React.ReactNode => formatMessage(comboBoxMessages[id])}
          id={formatMessage(messages.selectDimension)}
          key={JSON.stringify(dimensionsItems)}
          titleText={formatMessage(messages.selectDimension)}
          placeholder={formatMessage(messages.selectDimensionPlaceHolder)}
          helperText={helperText}
          items={dimensionsItems}
          light
          initialSelectedItem={value ? { id: value, text: value } : undefined}
          onChange={(changedItem): void => {
            delayedOnChange(
              changedItem
                ? {
                    ...cardConfig,
                    customContent: {
                      type: ThirdPartyContentTypes.DIMENSION,
                      value: changedItem.text,
                    },
                    dataSource: {
                      attributes: [
                        {
                          id: changedItem.text,
                          attribute: changedItem.text,
                        },
                      ],
                    },
                  }
                : // clearing input
                  {
                    ...omit(cardConfig, 'dataSource'),
                    customContent: {
                      type: ThirdPartyContentTypes.DIMENSION,
                    },
                  }
            );
          }}
        />
      )}
    </div>
  );
};

ThirdPartyCardEditContent.defaultProps = defaultProps;
export default ThirdPartyCardEditContent;
